import { createContext, useContext, useState, useEffect } from 'react';
import { setGlobalErrorHandler } from '../services/axiosConfig';
import { isProjectQuotaExceeded } from '../utils/isProjectQuotaExceeded';

const ErrorContext = createContext();

export function useError() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}

export function ErrorProvider({ children }) {
  const [errorState, setErrorState] = useState(null);
  const [projectQuotaState, setProjectQuotaState] = useState(null);

  // Fresh object each call so the modal's on-show tracking fires per rejection.
  const showProjectQuotaError = (data) => setProjectQuotaState({ ...data });
  const hideProjectQuotaError = () => setProjectQuotaState(null);

  const showError = ({ message, title = 'Something went wrong!', type = 'error' }) => {
    setErrorState({
      title,
      message,
      type,
    });
  };

  const hideError = () => {
    setErrorState(null);
  };

  const extractErrorMessage = (error) => {
    // Extract error message from common error response formats
    if (error?.response?.data?.user_error_message) {
      return error.response.data.user_error_message;
    }
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.response?.data?.error) {
      return error.response.data.error;
    }

    return 'Something went wrong. Please try again later.';
  };

  const showApiError = (
    error,
    customMessage = null,
    customTitle = 'Something went wrong!',
    fallbackMessage = 'Something went wrong. Please try again later.'
  ) => {
    if (isProjectQuotaExceeded(error)) {
      showProjectQuotaError(error.response?.data ?? error.data);
      return;
    }
    const message = customMessage || extractErrorMessage(error) || fallbackMessage;
    showError({ message, title: customTitle });
  };

  // Set up global error handler for axios
  useEffect(() => {
    setGlobalErrorHandler(showApiError);
  }, []);

  return (
    <ErrorContext.Provider
      value={{
        errorState,
        showError,
        hideError,
        showApiError,
        extractErrorMessage,
        projectQuotaState,
        showProjectQuotaError,
        hideProjectQuotaError,
      }}>
      {children}
    </ErrorContext.Provider>
  );
}
